import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Loader2, Image as ImageIcon } from 'lucide-react';
import { galleryApi } from '../services/api';
import { useSEO } from '../hooks/useSEO';
import galleryImage from '../assets/images/gallery.webp';

// ─── Lazy-loaded image component ───
const LazyImage = ({ src, alt, className, onClick }) => {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className={className} style={{ position: 'relative' }} onClick={onClick}>
      {!loaded && (
        <div className="absolute inset-0 animate-pulse" style={{ backgroundColor: 'var(--bg-secondary)' }} />
      )}
      {inView && (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.4s ease' }}
          onLoad={() => setLoaded(true)}
        />
      )}
    </div>
  );
};

const GalleryPage = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [images, setImages] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingImages, setLoadingImages] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [imageCache, setImageCache] = useState({});

  useSEO({
    title: 'Gallery | OlivOak Villa',
    description: 'Explore the beauty of OlivOak Villa through our photo gallery. View our luxury rooms, facilities, gardens, and more in Harare, Zimbabwe.'
  });

  // Fetch categories on mount
  useEffect(() => {
    (async () => {
      try {
        const cats = await galleryApi.getCategories();
        setCategories(cats || []);
      } catch (err) {
        console.error('Failed to load gallery categories:', err);
      } finally {
        setLoadingCategories(false);
      }
    })();
  }, []);

  // Fetch images when category changes
  const fetchImages = useCallback(async (catKey) => {
    if (imageCache[catKey]) { setImages(imageCache[catKey]); return; }
    setLoadingImages(true);
    try {
      const categoryId = catKey === 'all' ? null : catKey;
      const data = await galleryApi.getImages(categoryId, true);
      setImages(data || []);
      setImageCache(prev => ({ ...prev, [catKey]: data || [] }));
    } catch (err) {
      console.error('Failed to load gallery images:', err);
      setImages([]);
    } finally {
      setLoadingImages(false);
    }
  }, [imageCache]);

  useEffect(() => { fetchImages(selectedCategory); }, [selectedCategory, fetchImages]);

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
      {/* Hero Banner */}
      <section
        className="relative py-24"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('${galleryImage}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'top'
        }}
      >
        <div className="container text-center">
          <h1 className="hero-large mb-4" style={{ color: '#ffffff' }}>
            Gallery
          </h1>
          <p className="body-large" style={{ color: '#f0f0f0', maxWidth: '600px', margin: '0 auto' }}>
            Explore the beauty and elegance of OlivOak Villa
          </p>
        </div>
      </section>

      {/* Category Filters */}
      <section
        className="py-8 sticky top-20 z-40"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderBottom: '1px solid var(--border-light)'
        }}
      >
        <div className="container">
          {loadingCategories ? (
            <div className="flex justify-center">
              <Loader2 className="animate-spin" size={24} style={{ color: 'var(--olive-primary)' }} />
            </div>
          ) : (
            <div className="flex justify-center gap-4 flex-wrap">
              <button
                onClick={() => setSelectedCategory('all')}
                className="px-6 py-3 border transition-all"
                style={{
                  borderColor: selectedCategory === 'all' ? 'var(--interactive-base)' : 'var(--border-medium)',
                  backgroundColor: selectedCategory === 'all' ? 'var(--interactive-base)' : 'transparent',
                  color: selectedCategory === 'all' ? 'var(--bg-primary)' : 'var(--text-primary)',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '14px',
                  fontWeight: selectedCategory === 'all' ? 500 : 400,
                  cursor: 'pointer'
                }}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className="px-6 py-3 border transition-all"
                  style={{
                    borderColor: selectedCategory === cat.id ? 'var(--interactive-base)' : 'var(--border-medium)',
                    backgroundColor: selectedCategory === cat.id ? 'var(--interactive-base)' : 'transparent',
                    color: selectedCategory === cat.id ? 'var(--bg-primary)' : 'var(--text-primary)',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '14px',
                    fontWeight: selectedCategory === cat.id ? 500 : 400,
                    cursor: 'pointer'
                  }}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding">
        <div className="container">
          {loadingImages ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin" size={32} style={{ color: 'var(--olive-primary)' }} />
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-16">
              <ImageIcon size={48} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
              <p className="heading-4 mb-2" style={{ color: 'var(--text-secondary)' }}>No images yet</p>
              <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                Check back soon — we're adding photos of our beautiful property.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {images.map((image) => (
                <div
                  key={image.id}
                  className="hover-lift cursor-pointer"
                  onClick={() => setLightboxImage(image)}
                  style={{
                    overflow: 'hidden',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <LazyImage
                    src={image.url}
                    alt={image.alt || ''}
                    className="w-full h-80"
                  />
                  <div className="p-4" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      {image.gallery_categories?.name || ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-8 right-8 p-3"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid var(--border-light)',
              color: 'var(--text-primary)'
            }}
            onClick={() => setLightboxImage(null)}
          >
            <X size={24} />
          </button>
          <img
            src={lightboxImage.url}
            alt={lightboxImage.alt || ''}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
